import {Box, SxProps} from "@mui/system";
import {Button, CircularProgress, Typography} from "@mui/material";
import LoginTextField from "../LoginTextField/LoginTextField";
import HttpsIcon from '@mui/icons-material/Https';
import {FormEvent, useEffect, useState} from "react";
import { useRoutingSearchParams, navigate} from "../../app/services/routing";
import {useVerifyAccessCodeMutation} from "../../app/services/api";
import ErrorInfo from "../ErrorInfo/ErrorInfo";
import {SUPPORT_URL} from "../ContactText";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";

const formSx = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
};
const buttonBoxSx: SxProps = {
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "column",
    flex: 1,
};
const buttonSx: SxProps = {
    textTransform: "none",
    backgroundColor: "success.contrastText",
    color: "primary.contrastText",
    paddingY: "12px",

    "&:hover": {
        color: "primary.contrastText",
    },
};
const boxSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'space-between'
};


const InviteTokenBox = ({
}) => {
    const searchParams = useRoutingSearchParams();
    const inviteToken = searchParams.get("invite_id") || '';
    const [accessCode, setAccessCode] = useState("");
    const [currentForm, setCurrentForm] = useState('accessCode');
    const [incorrectAccessCode, setIncorrectAccessCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [verifyAccessCode, verifyAccessCodeResult] = useVerifyAccessCodeMutation();

    const handleSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        const target = ev.target as HTMLFormElement;

        if (!target?.checkValidity()) {
            setShowError(true);
            return;
        }
        if (!accessCode) {
            return;
        }

        setLoading(true);
        verifyAccessCode({inviteToken, accessCode}).unwrap()
            .then(() => setCurrentForm('redirect'))
            .catch((err: FetchBaseQueryError) => {
                switch (err.status) {
                    // TODO check for exact error code for incorrect access code
                    case 400:
                        setIncorrectAccessCode(true);
                        break;

                    // TODO check for exact error code for token expiration
                    case 403:
                        setCurrentForm('tokenExpired');
                        break;

                    default:
                        setCurrentForm('error');
                        break;
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        /* Disable error as user tries to enter the accessCode */
        setShowError(false);
    }, [accessCode]);

    const helperText = () => {
        if (incorrectAccessCode) {
            return 'Please enter correct access code.';
        }

        if (showError) {
            return 'This field is required.';
        }
        return 'Your 6-digit access code can be found in your email.';
    };

    const renderTokenExpiredForm = () => <ErrorInfo
        header={'The token has expired!'}
        description={'The token that you had received is no longer active.'}
        additionalInfo={'Please reach out to our sales team and mention the token id.'}
        contactSales={true}
        submitButton={
            <Box sx={buttonBoxSx}>
                <Button
                    id="redirect"
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={buttonSx}
                    component="a"
                    href="mailto:sales@nl-ix.net"
                >
                    <Typography variant="h5">Contact Sales</Typography>
                </Button>
            </Box>

        }
    ></ErrorInfo>;

    const renderErrorForm = () => <ErrorInfo
        header={'Oops! Something went wrong'}
        description={'Sorry, we could not log you in.'}
        additionalInfo={'This is quite the embarrassment. Please try again later.\n'}
        submitButton={
            <Box sx={buttonBoxSx}>
                <Button
                    id="redirect"
                    variant="contained"
                    color="success"
                    fullWidth
                    sx={buttonSx}
                    href={SUPPORT_URL}
                >
                    <Typography variant="h5">Contact Support</Typography>
                </Button>
            </Box>
        }
    ></ErrorInfo>;

    const content = () => {
        switch (currentForm) {
            case "tokenExpired":
                return renderTokenExpiredForm();

            case "error":
                return renderErrorForm();

            case "redirect":
                if (verifyAccessCodeResult.data?.invite?.resource?.start_url) {
                    navigate("redirect", { params: {next: verifyAccessCodeResult.data.invite.resource.start_url} });
                }
                return null;

            case "accessCode":
            default:
                return renderAccessCode();
        }
    };

    const renderAccessCode = () => (
        <Box component="form" sx={formSx} onSubmit={handleSubmit} noValidate>
            <Typography variant="h2" gutterBottom>
                Hello there&#33;
            </Typography>
            <Typography variant="body1" gutterBottom>
                Please enter the access token you received in your email to continue.
            </Typography>
            <Typography variant="body1" gutterBottom>
                Having trouble or your token isn’t working? We’re here to help, reach out at
                {' '}<a href="mailto:sales@nl-ix.net">sales@nl-ix.net</a> or give us a call on <a
                    href="tel:+31703120710">+31 (0)70 312 0710</a>.
            </Typography>
            <Box sx={boxSx}>
                <LoginTextField
                    id={"token"}
                    type="number"
                    label={"Access code:"}
                    placeholder={"000000"}
                    required
                    error={showError || incorrectAccessCode}
                    helperText={helperText()}
                    value={accessCode}
                    onChangeHandler={(ev: Event, value: string) => setAccessCode(value)}
                />
                <Button
                    id="submit-button"
                    variant="contained"
                    color="success"
                    fullWidth
                    type="submit"
                    disabled={loading}
                    startIcon={
                        loading ?
                            <CircularProgress size={24} color={'inherit'}/> :
                            <HttpsIcon sx={{marginBottom: '-4px'}}/>
                    }
                >
                    Verify code and continue
                </Button>
            </Box>
        </Box>);

    return  content();
};

export default InviteTokenBox;
